export const SEGMENT_LENGTH_EDIT_HISTORY_STORAGE_KEY =
  'nodon.design.segment-length-edit-history';

type SegmentLengthEditHistory = Record<string, number>;
type SegmentLengthStorage = Pick<Storage, 'getItem' | 'setItem'>;

export type SegmentLengthEditIdentity = {
  storeyId: string;
  shapeId: string;
  pointCount: number;
  startIndex: number;
  endIndex: number;
};

const getDefaultStorage = (): SegmentLengthStorage | undefined =>
  typeof window === 'undefined' ? undefined : window.sessionStorage;

const getSegmentKey = (identity: SegmentLengthEditIdentity): string =>
  JSON.stringify([
    identity.storeyId,
    identity.shapeId,
    identity.pointCount,
    identity.startIndex,
    identity.endIndex,
  ]);

const readHistory = (
  storage: SegmentLengthStorage | undefined,
): SegmentLengthEditHistory => {
  if (!storage) return {};
  try {
    const parsed: unknown = JSON.parse(
      storage.getItem(SEGMENT_LENGTH_EDIT_HISTORY_STORAGE_KEY) ?? '{}',
    );
    if (!parsed || typeof parsed !== 'object' || Array.isArray(parsed)) {
      return {};
    }
    return Object.fromEntries(
      Object.entries(parsed).filter(
        (entry): entry is [string, number] =>
          typeof entry[1] === 'number' && Number.isFinite(entry[1]),
      ),
    );
  } catch {
    return {};
  }
};

/** Read manual edit timestamps for every segment in one current polygon. */
export const getManualSegmentLengthTimestamps = (
  identity: Omit<SegmentLengthEditIdentity, 'startIndex' | 'endIndex'>,
  storage: SegmentLengthStorage | undefined = getDefaultStorage(),
): ReadonlyMap<number, number> => {
  const history = readHistory(storage);
  const timestamps = new Map<number, number>();
  for (let startIndex = 0; startIndex < identity.pointCount; startIndex += 1) {
    const endIndex = (startIndex + 1) % identity.pointCount;
    const timestamp =
      history[getSegmentKey({ ...identity, startIndex, endIndex })];
    if (timestamp !== undefined) timestamps.set(startIndex, timestamp);
  }
  return timestamps;
};

/** Store a monotonically ordered timestamp after a manual length edit succeeds. */
export const recordManualSegmentLengthEdit = (
  identity: SegmentLengthEditIdentity,
  storage: SegmentLengthStorage | undefined = getDefaultStorage(),
  now = Date.now(),
): number | undefined => {
  if (!storage) return undefined;
  const history = readHistory(storage);
  const latestTimestamp = Object.values(history).reduce(
    (latest, timestamp) => Math.max(latest, timestamp),
    0,
  );
  const timestamp = Math.max(now, latestTimestamp + 1);
  history[getSegmentKey(identity)] = timestamp;
  try {
    storage.setItem(
      SEGMENT_LENGTH_EDIT_HISTORY_STORAGE_KEY,
      JSON.stringify(history),
    );
    return timestamp;
  } catch {
    return undefined;
  }
};
